import { RuleNode } from "@repo/api";
import { CorrelationRuleItem } from "@repo/api/schemas/correlationRules.schema";

export interface CorrelationRuleUserField {
  id: string;
  field: string;
  initialChar: string;
}

export interface CorrelationRule {
  id: string;
  serverRuleId?: number;
  accountField: string;
  userFields: CorrelationRuleUserField[];
}

export interface UnsupportedRule {
  id: string;
  serverRuleId?: number;
  unsupported: true;
  raw: RuleNode;
}

export type DecodedRule = CorrelationRule | UnsupportedRule;

export function isUnsupported(rule: DecodedRule): rule is UnsupportedRule {
  return (rule as UnsupportedRule).unsupported === true;
}

let nextId = 0;
function newId(): string {
  nextId += 1;
  return `cr-${Date.now()}-${nextId}`;
}

function isOperator(node: RuleNode): node is { op: string; args: RuleNode[] } {
  return (node as { op?: string }).op !== undefined;
}

function isRef(node: RuleNode): node is { ref: string } {
  return (node as { ref?: string }).ref !== undefined;
}

function isConst(node: RuleNode): node is { const: number } {
  return (node as { const?: number }).const !== undefined;
}

function splitRef(ref: string): { entity: string; field: string } | null {
  const dot = ref.indexOf(".");
  if (dot < 0) return null;
  return { entity: ref.slice(0, dot), field: ref.slice(dot + 1) };
}

/** Encode a flat UI rule into the server AST shape. */
export function encodeRule(rule: CorrelationRule): RuleNode {
  const userArgs: RuleNode[] = rule.userFields.map((uf) => ({
    op: "substring",
    args: [
      { ref: `user.${uf.field}` },
      { const: 0 },
      { const: Number(uf.initialChar) || 0 },
    ],
  }));

  const userSide: RuleNode =
    userArgs.length === 1 ? userArgs[0] : { op: "concat", args: userArgs };

  return {
    op: "eq",
    args: [userSide, { ref: `account.${rule.accountField}` }],
  };
}

/**
 * Try to decode a server AST rule into the flat UI shape.
 * Returns an UnsupportedRule if the AST doesn't match our template — those
 * are kept verbatim so PUT-replace round-trips them without data loss.
 *
 * Accepts both arg orderings: eq(account, user-side) and eq(user-side, account).
 */
export function decodeRule(node: RuleNode): DecodedRule {
  const id = newId();
  const unsupported: UnsupportedRule = { id, unsupported: true, raw: node };

  if (!isOperator(node) || node.op !== "eq" || node.args.length !== 2) {
    return unsupported;
  }

  const [a, b] = node.args;
  const candidatePairs: Array<[RuleNode, RuleNode]> = [
    [a, b],
    [b, a],
  ];

  for (const [accountSide, userSide] of candidatePairs) {
    if (!isRef(accountSide)) continue;
    const accountSplit = splitRef(accountSide.ref);
    if (!accountSplit || accountSplit.entity !== "account") continue;

    // userSide may be a single substring node OR concat of substring nodes
    const userArgs: RuleNode[] =
      isOperator(userSide) && userSide.op === "concat"
        ? userSide.args
        : [userSide];

    const userFields: CorrelationRuleUserField[] = [];
    let allOk = true;

    for (const ua of userArgs) {
      if (
        !isOperator(ua) ||
        ua.op !== "substring" ||
        ua.args.length !== 3 ||
        !isRef(ua.args[0]) ||
        !isConst(ua.args[1]) ||
        !isConst(ua.args[2])
      ) {
        allOk = false;
        break;
      }
      const userSplit = splitRef(ua.args[0].ref);
      if (!userSplit || userSplit.entity !== "user") {
        allOk = false;
        break;
      }
      userFields.push({
        id: newId(),
        field: userSplit.field,
        initialChar: String((ua.args[2] as { const: number }).const),
      });
    }

    if (allOk && userFields.length > 0) {
      return { id, accountField: accountSplit.field, userFields };
    }
  }

  return unsupported;
}

export function decodeAll(items: CorrelationRuleItem[]): DecodedRule[] {
  return items.map((item) => {
    const decoded = decodeRule(item.rule);
    decoded.serverRuleId = item.ruleId;
    return decoded;
  });
}

/** Encode a list of decoded rules back to AST. Unsupported rules are passed through unchanged. */
export function encodeAll(rules: DecodedRule[]): RuleNode[] {
  return rules.map((r) => (isUnsupported(r) ? r.raw : encodeRule(r)));
}
